import { useEffect, useRef, useState } from 'react'
import { getCookie } from 'cookies-next'
import { AD_TESTING_COOKIE } from '@/constants/cookies'
import { logger } from '@/utils/logging'
import { fetchAndParseVast, parseVastXml } from './parseVast'
import { fetchAndParseVmap } from './parseVmap'
import type { AdBreak, AdPlaybackState, ResolvedAdBreak, VastAd } from './types'

const AD_TESTING_FORCE_PARAMS = '&test=true&force_pre_roll=true'

interface UseAdScheduleOptions {
  /** VMAP URL from the asset, or undefined if no ads. */
  vmapUrl: string | undefined
  /** Whether ads are enabled for this player instance. */
  enableAds: boolean
  /** Ref to the content video element — used to pause/resume and monitor time for mid-rolls. */
  videoRef: React.RefObject<HTMLVideoElement | null>
  /** Called when an ad break starts playing. */
  onAdStarted?: () => void
  /** Called when an ad break finishes (natural completion). */
  onAdFinished?: () => void
  /** Called when the user skips an ad. */
  onAdSkipped?: () => void
}

interface UseAdScheduleReturn {
  /** Current ad playback state. */
  adState: AdPlaybackState
  /** The currently-playing ad, or null. */
  currentAd: VastAd | null
  /** Whether content playback should be blocked (pre-roll pending or ad playing). */
  isContentBlocked: boolean
  /** Called by the ad overlay when the ad video ends naturally. */
  onAdVideoEnded: () => void
  /** Called by the ad overlay when the user clicks skip. */
  onAdVideoSkipped: () => void
}

/**
 * Orchestrates the ad lifecycle for the HLS.js player.
 *
 * Handles pre-roll (before content starts), mid-roll (during content at specific times),
 * and post-roll (after content ends) ad breaks. Fetches VMAP + VAST on mount,
 * resolves all ad breaks, and triggers them at the appropriate times.
 */
export function useAdSchedule({
  vmapUrl,
  enableAds,
  videoRef,
  onAdStarted,
  onAdFinished,
  onAdSkipped,
}: UseAdScheduleOptions): UseAdScheduleReturn {
  const [adState, setAdState] = useState<AdPlaybackState>('idle')
  const [currentAd, setCurrentAd] = useState<VastAd | null>(null)
  const [isContentBlocked, setIsContentBlocked] = useState(false)

  const adBreaksRef = useRef<ResolvedAdBreak[]>([])
  const preRollCompleteRef = useRef(false)
  const contentTimeBeforeAdRef = useRef(0)
  const adStateRef = useRef<AdPlaybackState>(adState)
  adStateRef.current = adState

  const onAdStartedRef = useRef(onAdStarted)
  onAdStartedRef.current = onAdStarted

  // Stable ref-based function so effects don't need it as a dependency.
  const playAdBreakRef = useRef((adBreak: ResolvedAdBreak) => {
    adBreak.played = true
    setCurrentAd(adBreak.ad)
    setAdState('playing')
    setIsContentBlocked(true)
    onAdStartedRef.current?.()
  })

  // Fetch and resolve VMAP + VAST on mount
  useEffect(() => {
    if (!enableAds || !vmapUrl) {
      preRollCompleteRef.current = true
      return
    }

    const abortController = new AbortController()
    let cancelled = false

    const resolveAds = async () => {
      try {
        const adUrl = applyAdTestingParams(vmapUrl)
        const adBreaks = await fetchAndParseVmap(adUrl, abortController.signal)
        if (cancelled) return

        const hasPreRoll = adBreaks.some((b) => b.position === 'pre')
        if (hasPreRoll) {
          setIsContentBlocked(true)
        }

        const resolved = await resolveAllAdBreaks(adBreaks, abortController.signal)
        if (cancelled) return

        adBreaksRef.current = resolved

        // Auto-play pre-roll if one exists
        const preRoll = resolved.find((b) => b.position === 'pre' && !b.played)
        if (preRoll) {
          playAdBreakRef.current(preRoll)
        } else {
          preRollCompleteRef.current = true
          setIsContentBlocked(false)
        }
      } catch (error) {
        if (!cancelled) {
          logger().warn('Failed to resolve ad schedule', {}, error)
          preRollCompleteRef.current = true
          setIsContentBlocked(false)
        }
      }
    }

    resolveAds()

    return () => {
      cancelled = true
      abortController.abort()
    }
  }, [vmapUrl, enableAds])

  // Monitor content time for mid-roll ad breaks
  useEffect(() => {
    if (!enableAds) return
    const video = videoRef.current
    if (!video) return

    const MID_ROLL_CHECK_TOLERANCE_SECONDS = 1

    const handleTimeUpdate = () => {
      if (adStateRef.current !== 'idle') return
      const time = video.currentTime

      const pendingMidRoll = adBreaksRef.current.find(
        (b) =>
          typeof b.position === 'number' &&
          !b.played &&
          time >= b.position &&
          time < b.position + MID_ROLL_CHECK_TOLERANCE_SECONDS,
      )

      if (pendingMidRoll) {
        contentTimeBeforeAdRef.current = video.currentTime
        video.pause()
        playAdBreakRef.current(pendingMidRoll)
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => video.removeEventListener('timeupdate', handleTimeUpdate)
  }, [enableAds, videoRef])

  // Monitor content ended for post-roll
  useEffect(() => {
    if (!enableAds) return
    const video = videoRef.current
    if (!video) return

    const handleEnded = () => {
      const postRoll = adBreaksRef.current.find((b) => b.position === 'post' && !b.played)
      if (postRoll) {
        playAdBreakRef.current(postRoll)
      }
    }

    video.addEventListener('ended', handleEnded)
    return () => video.removeEventListener('ended', handleEnded)
  }, [enableAds, videoRef])

  function finishAd(skipped: boolean) {
    setCurrentAd(null)
    setAdState('idle')

    if (skipped) {
      onAdSkipped?.()
    } else {
      onAdFinished?.()
    }

    const isPreRoll = !preRollCompleteRef.current
    if (isPreRoll) {
      preRollCompleteRef.current = true
    }

    // Check if there are more ad breaks queued (e.g., ad pod with multiple ads)
    // For now, we play one ad per break. This can be extended for ad pods.
    setIsContentBlocked(false)

    // Resume content after mid-roll
    const video = videoRef.current
    if (video && !isPreRoll && contentTimeBeforeAdRef.current > 0) {
      video.currentTime = contentTimeBeforeAdRef.current
      video.play().catch((error) => {
        if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError')) return
        logger().warn('Content resume after ad failed', {}, error)
      })
      contentTimeBeforeAdRef.current = 0
    }
  }

  const onAdVideoEnded = () => finishAd(false)
  const onAdVideoSkipped = () => finishAd(true)

  return {
    adState,
    currentAd,
    isContentBlocked,
    onAdVideoEnded,
    onAdVideoSkipped,
  }
}

async function resolveAllAdBreaks(adBreaks: AdBreak[], signal: AbortSignal): Promise<ResolvedAdBreak[]> {
  const resolved: ResolvedAdBreak[] = []

  for (const adBreak of adBreaks) {
    const ad = await resolveAdBreak(adBreak, signal)
    if (ad) {
      resolved.push({ position: adBreak.position, ad, played: false })
    }
  }

  return resolved
}

async function resolveAdBreak(adBreak: AdBreak, signal: AbortSignal): Promise<VastAd | null> {
  try {
    if (adBreak.vastInline) {
      return parseVastXml(adBreak.vastInline)
    }
    if (adBreak.vastUri) {
      return fetchAndParseVast(adBreak.vastUri, signal)
    }
    return null
  } catch (error) {
    logger().warn('Failed to resolve VAST for ad break', { position: adBreak.position }, error)
    return null
  }
}

function applyAdTestingParams(vmapUrl: string): string {
  const cookie = getCookie(AD_TESTING_COOKIE)
  if (cookie === 'force') {
    return vmapUrl + AD_TESTING_FORCE_PARAMS
  }
  return vmapUrl
}
